import {
  VicmapAddressQuery,
  VicmapAddressResponse,
  AddressLookupResult,
  VicmapPlanningZoneResult,
  VicmapPlanningOverlayResult,
  PlanningInformation,
} from '../types/vicGov';

const VICMAP_ADDRESS_URL =
  'https://services-ap1.arcgis.com/P744lA0wf4LlBZ84/arcgis/rest/services/Vicmap_Address/FeatureServer/0/query';

const VICMAP_PLANNING_BASE =
  'https://services-ap1.arcgis.com/P744lA0wf4LlBZ84/ArcGIS/rest/services/Vicmap_Planning/FeatureServer';

const VICMAP_PARCEL_URL =
  'https://services-ap1.arcgis.com/P744lA0wf4LlBZ84/ArcGIS/rest/services/Vicmap_Parcel/FeatureServer/0';

// Layer IDs for planning zones and overlays (these may need adjustment based on actual service)
const PLANNING_ZONES_LAYER = 0;
const PLANNING_OVERLAYS_LAYER = 1;

// Interface for address suggestions
export interface AddressSuggestion {
  id: string;
  label: string;
  address: string;
  locality: string;
  postcode: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

// Interface for parcel/estate results
export interface ParcelResult {
  pfi: string;
  planNumber: string;
  lotNumber: string;
  spi: string;
  allotment: string;
  block: string;
  subdivision: string;
  distance: number; // meters from search point
  coordinates: {
    lat: number;
    lng: number;
  };
}

/**
 * Address Autocomplete: Search for addresses matching partial input
 * Uses flexible word-based matching similar to LASSI
 */
export async function suggestAddresses(searchText: string): Promise<AddressSuggestion[]> {
  if (searchText.trim().length < 2) {
    return [];
  }

  const searchUpper = escapeSql(searchText.toUpperCase());
  const words = searchUpper.split(/\s+/).filter((w) => w.length > 0);

  let whereClause: string;

  if (words.length === 1) {
    // Single word: flexible matching on address or locality
    const word = words[0];
    whereClause = `(UPPER(ezi_address) LIKE '${word}%' OR UPPER(locality_name) LIKE '${word}%')`;
  } else if (words.length === 2) {
    // Two words: could be "65 MAIN" or "MAIN WINCHELSEA"
    // Try street number + street name, or street + suburb
    const [word1, word2] = words;
    whereClause = `(
      (UPPER(ezi_address) LIKE '${word1}%' AND UPPER(ezi_address) LIKE '%${word2}%') OR
      (UPPER(ezi_address) LIKE '%${word1}%' AND UPPER(locality_name) LIKE '${word2}%')
    )`;
  } else {
    // Three+ words: "65 MAIN STREET" or "65 MAIN WINCHELSEA"
    // Build AND conditions for each word
    const wordConditions = words
      .map((word) => `(UPPER(ezi_address) LIKE '%${word}%' OR UPPER(locality_name) LIKE '%${word}%')`)
      .join(' AND ');
    whereClause = `(${wordConditions})`;
  }

  const params = new URLSearchParams({
    where: whereClause,
    outFields: 'OBJECTID,ezi_address,locality_name,postcode,house_number_1,road_name,road_type',
    returnGeometry: 'true',
    f: 'json',
    outSR: '4326',
    resultRecordCount: '20',
  });

  try {
    const response = await fetch(`${VICMAP_ADDRESS_URL}?${params.toString()}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Address suggest API error: ${response.status}`, errorText);
      return [];
    }

    const data = await response.json();

    if (data.error) {
      console.error('API returned error:', data.error);
      return [];
    }

    if (!data.features || data.features.length === 0) {
      return [];
    }

    // Map results to suggestion format
    return data.features.map((feature: any) => {
      const attrs = feature.attributes;

      // Use ezi_address field (full formatted address)
      const eziAddress = (attrs.ezi_address || '').toUpperCase();
      const locality = (attrs.locality_name || '').toUpperCase();
      const postcode = attrs.postcode || '';
      const objectId = attrs.OBJECTID || attrs.ufi || Math.random();

      // Fallback: if ezi_address is empty, build from components
      let finalAddress = eziAddress;
      if (!finalAddress && attrs.house_number_1 && attrs.road_name) {
        const roadType = attrs.road_type ? ` ${attrs.road_type.toUpperCase()}` : '';
        finalAddress = `${attrs.house_number_1} ${attrs.road_name.toUpperCase()}${roadType}`.trim();
      }

      const finalLabel = `${finalAddress}, ${locality} VIC ${postcode}`;

      return {
        id: String(objectId),
        label: finalLabel,
        address: finalAddress,
        locality: locality,
        postcode: postcode,
        coordinates: {
          lat: feature.geometry.y,
          lng: feature.geometry.x,
        },
      };
    });
  } catch (error) {
    console.error('Address suggest error:', error);
    return [];
  }
}

/**
 * Search for parcels/estates within a radius of a point
 * Returns plan numbers, lot numbers, and Standard Parcel Identifiers (SPI)
 */
export async function searchParcelsNearPoint(
  lat: number,
  lng: number,
  radiusMeters: number = 500
): Promise<ParcelResult[]> {
  // Create a point geometry in WGS84
  const geometryParam = JSON.stringify({
    x: lng,
    y: lat,
  });

  const params = new URLSearchParams({
    geometry: geometryParam,
    geometryType: 'esriGeometryPoint',
    inSR: '4326', // Input coordinates are in WGS84
    spatialRel: 'esriSpatialRelIntersects',
    distance: radiusMeters.toString(),
    units: 'esriSRUnit_Meter',
    outFields: 'parcel_pfi,parcel_plan_number,parcel_lot_number,parcel_spi,parcel_allotment,parcel_block,parcel_subdivision',
    returnGeometry: 'true',
    returnCentroid: 'false',
    f: 'json',
    outSR: '4326', // Return geometry in WGS84
    resultRecordCount: '10', // Limit to 10 results
  });

  try {
    const url = `${VICMAP_PARCEL_URL}/query?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Parcel search API error: ${response.status}`, errorText);
      return [];
    }

    const data = await response.json();

    if (data.error) {
      console.error('API returned error:', data.error);
      console.error('Query URL:', url);
      return [];
    }

    if (!data.features || data.features.length === 0) {
      return [];
    }

    // Map results to ParcelResult format
    const parcels = data.features
      .map((feature: any) => {
        const attrs = feature.attributes;

        // Calculate distance from search point to parcel centroid
        const parcelLat = feature.geometry?.y || lat;
        const parcelLng = feature.geometry?.x || lng;
        const distance = calculateDistance(lat, lng, parcelLat, parcelLng);

        return {
          pfi: attrs.parcel_pfi || '',
          planNumber: attrs.parcel_plan_number || '',
          lotNumber: attrs.parcel_lot_number || '',
          spi: attrs.parcel_spi || '',
          allotment: attrs.parcel_allotment || '',
          block: attrs.parcel_block || '',
          subdivision: attrs.parcel_subdivision || '',
          distance: Math.round(distance),
          coordinates: {
            lat: parcelLat,
            lng: parcelLng,
          },
        };
      })
      .filter((parcel: { planNumber: string }) => parcel.planNumber); // Only return parcels with plan numbers

    // Sort by lot number (numerically, highest to lowest)
    parcels.sort((a: { lotNumber: string }, b: { lotNumber: string }) => {
      const lotA = parseLotNumber(a.lotNumber);
      const lotB = parseLotNumber(b.lotNumber);
      return lotB - lotA; // Reversed for descending order
    });

    return parcels;
  } catch (error) {
    console.error('Parcel search error:', error);
    return [];
  }
}

/**
 * Calculate distance between two points in meters (Haversine formula)
 */
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Extract numeric value from lot number string for sorting
 * Examples: "39" -> 39, "Lot 5" -> 5, "1-10" -> 1, "A" -> Infinity
 */
function parseLotNumber(lotNumber: string): number {
  if (!lotNumber) return Infinity; // Put empty lot numbers at the end

  // Remove common prefixes like "Lot ", "LOT ", etc.
  const cleaned = lotNumber.replace(/^(lot|LOT)\s*/i, '').trim();

  // Extract first number (handles ranges like "1-10" -> 1)
  const match = cleaned.match(/^\d+/);

  if (match) {
    return parseInt(match[0], 10);
  }

  // If no number found (e.g., "A", "B"), put at the end
  return Infinity;
}

/**
 * Step 1: Resolve address to coordinates using Vicmap Address API
 * Updated to use exact OBJECTID match when available from autocomplete
 */
export async function resolveAddress(
  query: VicmapAddressQuery
): Promise<{ lat: number; lng: number; fullAddress: string; lga: string }> {
  // Build WHERE clause for address matching
  const whereClause = buildAddressWhereClause(query);

  const params = new URLSearchParams({
    where: whereClause,
    outFields: 'ezi_address,locality_name,postcode,lga_code',
    returnGeometry: 'true',
    f: 'json',
    outSR: '4326',
  });

  try {
    const response = await fetch(`${VICMAP_ADDRESS_URL}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`Vicmap Address API error: ${response.status}`);
    }

    const data: VicmapAddressResponse = await response.json();

    if (!data.features || data.features.length === 0) {
      throw new Error('No address matches found');
    }

    // Get best match (first result)
    const bestMatch = data.features[0];
    const attrs = bestMatch.attributes;

    return {
      lat: bestMatch.geometry.y,
      lng: bestMatch.geometry.x,
      fullAddress: `${attrs.EZI_ADDRESS}, ${attrs.LOCALITY_NAME} VIC ${attrs.POSTCODE}`,
      lga: attrs.LGA_NAME || '',
    };
  } catch (error) {
    console.error('Address resolution error:', error);
    throw new Error('Failed to resolve address. Please check the address and try again.');
  }
}

/**
 * Resolve address directly from a selected suggestion
 * This bypasses parsing and uses exact coordinates from autocomplete
 */
export async function resolveAddressFromSuggestion(
  suggestion: AddressSuggestion
): Promise<AddressLookupResult> {
  // We already have coordinates, just need to get planning info and LGA
  const { lat, lng } = suggestion.coordinates;

  // Get LGA by querying with OBJECTID if available, otherwise use coordinates
  const params = new URLSearchParams({
    geometry: JSON.stringify({
      x: lng,
      y: lat,
      spatialReference: { wkid: 4326 },
    }),
    geometryType: 'esriGeometryPoint',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'lga_code',
    returnGeometry: 'false',
    f: 'json',
    inSR: '4326',
    resultRecordCount: '1',
  });

  let lga = '';
  try {
    const response = await fetch(`${VICMAP_ADDRESS_URL}?${params.toString()}`);
    if (response.ok) {
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        lga = data.features[0].attributes.lga_code || '';
      }
    }
  } catch (error) {
    console.warn('Could not fetch LGA:', error);
  }

  // Get planning information
  const planning = await getPlanningInformation(lat, lng);

  return {
    address: suggestion.label,
    coordinates: { lat, lng },
    planning,
    lga,
  };
}

/**
 * Step 2: Query planning zones at a specific point
 */
async function queryPlanningZones(
  lat: number,
  lng: number
): Promise<VicmapPlanningZoneResult | null> {
  const geometryParam = JSON.stringify({
    x: lng,
    y: lat,
    spatialReference: { wkid: 4326 },
  });

  const params = new URLSearchParams({
    geometry: geometryParam,
    geometryType: 'esriGeometryPoint',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'ZONE_CODE,ZONE_DESCRIPTION,PLANNING_SCHEME,LGA_NAME',
    returnGeometry: 'false',
    f: 'json',
    inSR: '4326',
  });

  try {
    const response = await fetch(
      `${VICMAP_PLANNING_BASE}/${PLANNING_ZONES_LAYER}/query?${params.toString()}`
    );

    if (!response.ok) {
      console.warn(`Planning zones query failed: ${response.status}`);
      return null;
    }

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      return data.features[0] as VicmapPlanningZoneResult;
    }

    return null;
  } catch (error) {
    console.error('Planning zones query error:', error);
    return null;
  }
}

/**
 * Step 3: Query planning overlays at a specific point
 */
async function queryPlanningOverlays(
  lat: number,
  lng: number
): Promise<VicmapPlanningOverlayResult[]> {
  const geometryParam = JSON.stringify({
    x: lng,
    y: lat,
    spatialReference: { wkid: 4326 },
  });

  const params = new URLSearchParams({
    geometry: geometryParam,
    geometryType: 'esriGeometryPoint',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'OVERLAY_CODE,OVERLAY_NAME,SCHEME_CODE',
    returnGeometry: 'false',
    f: 'json',
    inSR: '4326',
  });

  try {
    const response = await fetch(
      `${VICMAP_PLANNING_BASE}/${PLANNING_OVERLAYS_LAYER}/query?${params.toString()}`
    );

    if (!response.ok) {
      console.warn(`Planning overlays query failed: ${response.status}`);
      return [];
    }

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      return data.features as VicmapPlanningOverlayResult[];
    }

    return [];
  } catch (error) {
    console.error('Planning overlays query error:', error);
    return [];
  }
}

/**
 * Step 4: Aggregate planning information
 */
async function getPlanningInformation(
  lat: number,
  lng: number
): Promise<PlanningInformation> {
  // Query zones and overlays in parallel
  const [zoneResult, overlayResults] = await Promise.all([
    queryPlanningZones(lat, lng),
    queryPlanningOverlays(lat, lng),
  ]);

  const planning: PlanningInformation = {
    overlays: [],
  };

  if (zoneResult) {
    planning.zone = {
      code: zoneResult.attributes.ZONE_CODE,
      description: zoneResult.attributes.ZONE_DESCRIPTION,
      schemeName: zoneResult.attributes.PLANNING_SCHEME,
      lgaName: zoneResult.attributes.LGA_NAME,
    };
  }

  if (overlayResults.length > 0) {
    planning.overlays = overlayResults.map((overlay) => ({
      code: overlay.attributes.OVERLAY_CODE,
      description: overlay.attributes.OVERLAY_NAME,
      schemeReference: overlay.attributes.SCHEME_CODE,
    }));
  }

  return planning;
}

/**
 * Main entry point: Complete address lookup with planning information
 */
export async function lookupVictorianAddress(
  query: VicmapAddressQuery
): Promise<AddressLookupResult> {
  // Step 1: Resolve address to coordinates
  const { lat, lng, fullAddress, lga } = await resolveAddress(query);

  // Step 2 & 3: Get planning information (zones + overlays)
  const planning = await getPlanningInformation(lat, lng);

  return {
    address: fullAddress,
    coordinates: { lat, lng },
    planning,
    lga,
  };
}

/**
 * Helper: Build WHERE clause for address matching
 */
function buildAddressWhereClause(query: VicmapAddressQuery): string {
  const conditions: string[] = [];

  // Try to match using ezi_address field (full address string) - Vicmap uses lowercase
  const fullAddressQuery = `${query.streetNumber} ${query.streetName}, ${query.locality}`;
  conditions.push(`UPPER(ezi_address) LIKE '%${escapeSql(fullAddressQuery.toUpperCase())}%'`);

  // Add postcode if provided
  if (query.postcode) {
    conditions.push(`postcode = '${escapeSql(query.postcode)}'`);
  }

  return conditions.join(' AND ');
}

/**
 * Helper: Escape SQL special characters
 */
function escapeSql(value: string): string {
  return value.replace(/'/g, "''");
}

/**
 * Helper: Parse user address input into structured query
 */
export function parseAddressInput(input: string): VicmapAddressQuery | null {
  // Simple parser for "123 Smith Street, Preston VIC 3072" format
  const trimmed = input.trim();

  // Try to extract components
  const parts = trimmed.split(',').map((p) => p.trim());

  if (parts.length < 2) {
    return null;
  }

  // First part: "123 Smith Street"
  const streetParts = parts[0].split(' ');
  if (streetParts.length < 2) {
    return null;
  }

  const streetNumber = streetParts[0];
  const streetName = streetParts.slice(1).join(' ');

  // Second part: "Preston" or "Preston VIC 3072"
  let locality = parts[1];
  let postcode: string | undefined;

  // Check if there's a postcode in the locality part
  const postcodeMatch = locality.match(/\b(\d{4})\b/);
  if (postcodeMatch) {
    postcode = postcodeMatch[1];
    locality = locality.replace(/VIC/i, '').replace(/\d{4}/, '').trim();
  }

  // Check third part for postcode if not found yet
  if (!postcode && parts.length > 2) {
    const thirdPart = parts[2].trim();
    const postcodeMatch2 = thirdPart.match(/\b(\d{4})\b/);
    if (postcodeMatch2) {
      postcode = postcodeMatch2[1];
    }
  }

  return {
    streetNumber,
    streetName,
    locality,
    postcode,
  };
}
